/**
 * ═══════════════════════════════════════════════════════════════════
 * FLOATING BOTTOM SHEET - Feature Info Component
 * A modern, draggable bottom sheet for displaying GIS feature information.
 * ═══════════════════════════════════════════════════════════════════
 */

/**
 * BottomSheet class - Creates and manages a floating bottom sheet UI component.
 * 
 * @example
 * const sheet = new BottomSheet();
 * 
 * sheet.show({
 *   title: "القطعة رقم 123",
 *   status: { label: "منطقة C", type: "c" },
 *   keyMetric: { label: "المساحة", value: "500.25 م²", icon: "fa-ruler-combined" },
 *   attributes: [
 *     { key: "المحافظة", value: "رام الله" },
 *     { key: "التجمع", value: "البيرة" }
 *   ]
 * });
 * 
 * sheet.onAction('report', () => generateReport());
 * sheet.onAction('navigate', () => startNavigation());
 */
class BottomSheet {
  constructor(options = {}) {
    this.options = {
      containerId: 'featureBottomSheet',
      bottomOffset: 70, // Offset from bottom to avoid overlapping UI
      expandedHeight: 80, // Percentage of viewport height when expanded
      ...options
    };

    this.isExpanded = false;
    this.isLocked = false; // ⚡ Flag to prevent closing during critical actions
    this.isDragging = false;
    this.startY = 0;
    this.currentY = 0;
    this.data = null;
    this.callbacks = {
      streetview: null,
      zoom: null,
      report: null,
      hide: null,
      navigate: null,
      close: null
    };

    // ⚡ Drag State Tracking
    this.velocity = 0;
    this.lastTimestamp = 0;
    this.lastY = 0;
    this.isClosing = false; // Flag if we are closing via drag

    this._init();
  }

  /**
   * Initialize the bottom sheet - create DOM and attach events
   */
  _init() {
    this._createDOM();
    this._attachEvents();
  }

  /**
   * Create the bottom sheet DOM structure
   */
  _createDOM() {
    // Check if already exists
    if (document.getElementById(this.options.containerId)) {
      this.container = document.getElementById(this.options.containerId);
      this._cacheElements();
      return;
    }

    const html = `
      <div id="${this.options.containerId}" class="bottom-sheet" dir="rtl">
        <!-- Close Button -->
        <button class="bottom-sheet-close" type="button" title="إغلاق">
          <i class="fas fa-times"></i>
        </button>
        
        <!-- Drag Handle -->
        <div class="bottom-sheet-handle"></div>
        
        <!-- Content Container -->
        <div class="bottom-sheet-content">
          
          <!-- Summary Section (Always Visible) -->
          <div class="bottom-sheet-summary">
            <div class="bottom-sheet-header">
              <h3 class="bottom-sheet-title"></h3>
              <p class="bottom-sheet-subtitle"></p>
              <span class="bottom-sheet-badge"></span>
            </div>
            <div class="bottom-sheet-metric">
              <div class="bottom-sheet-metric-icon">
                <i class="fas fa-ruler-combined"></i>
              </div>
              <div class="bottom-sheet-metric-content">
                <span class="bottom-sheet-metric-label"></span>
                <span class="bottom-sheet-metric-value"></span>
              </div>
            </div>
          </div>
          
          <!-- Action Buttons Container -->
          <div class="bottom-sheet-actions"></div>
          
          <!-- Details Section (Visible when expanded) -->
          <div class="bottom-sheet-details"></div>
          
        </div>
      </div>
    `;

    document.body.insertAdjacentHTML('beforeend', html);
    this.container = document.getElementById(this.options.containerId);
    this._cacheElements();

    // Initial Render of default actions if any
    this.renderActions(this.options.actions || this._getDefaultActions());
  }

  /**
   * Cache DOM element references
   */
  _cacheElements() {
    this.handle = this.container.querySelector('.bottom-sheet-handle');
    this.content = this.container.querySelector('.bottom-sheet-content');
    this.summary = this.container.querySelector('.bottom-sheet-summary');
    this.titleEl = this.container.querySelector('.bottom-sheet-title');
    this.subtitleEl = this.container.querySelector('.bottom-sheet-subtitle');
    this.metricIcon = this.container.querySelector('.bottom-sheet-metric-icon i');
    this.metricLabel = this.container.querySelector('.bottom-sheet-metric-label');
    this.metricValue = this.container.querySelector('.bottom-sheet-metric-value');
    this.actionsEl = this.container.querySelector('.bottom-sheet-actions');
    this.detailsEl = this.container.querySelector('.bottom-sheet-details');
    this.closeBtn = this.container.querySelector('.bottom-sheet-close');
  }

  /**
   * Attach event listeners
   */
  _attachEvents() {
    // Handle drag - Touch & Mouse events with delegation to support dynamic content
    const handleDragStart = (e) => {
      if (e.target.closest('.bottom-sheet-handle') || e.target.closest('.bottom-sheet-summary')) {
        this._onDragStart(e);
      }
    };

    this.container.addEventListener('touchstart', handleDragStart, { passive: true });
    this.container.addEventListener('mousedown', handleDragStart);

    document.addEventListener('touchmove', this._onDragMove.bind(this), { passive: false });
    document.addEventListener('mousemove', this._onDragMove.bind(this));
    document.addEventListener('touchend', this._onDragEnd.bind(this), { passive: true });
    document.addEventListener('mouseup', this._onDragEnd.bind(this));

    // Use event delegation on container for elements that may be rebuilt
    const stopProp = (e) => e.stopPropagation();
    ['click', 'mousedown', 'touchstart', 'pointerdown'].forEach(evt => {
      this.container.addEventListener(evt, stopProp, { passive: evt === 'touchstart' });
    });

    this.container.addEventListener('click', (e) => {
      // Handle action buttons (including expand)
      const btn = e.target.closest('[data-action]');
      if (btn) {
        const action = btn.dataset.action;

        // Special handling for expand button (built-in)
        if (action === 'expand') {
          this.expand();
          return;
        }

        // Call registered callbacks
        if (this.callbacks[action]) {
          this.callbacks[action](this.data);
        }
        return;
      }

      // Handle close button (X) - clears everything including highlight
      if (e.target.closest('.bottom-sheet-close')) {
        this.close();
        return;
      }
    });

    // Click outside behavior:
    // User requested to CLOSE the sheet when clicking anywhere outside.
    document.addEventListener('click', (e) => {
      // If visible (not just expanded) and click is outside container
      if (this.isVisible() && !this.container.contains(e.target)) {

        // ⚡ REGIONAL LOGIC: Check if sheet is manually locked
        if (this.isLocked) return;

        // ⚡ FIX: Don't close if clicking on persistent UI elements or map
        // This allows interacting with the sidebar, header, and choosing other map features
        const exemptSelectors = [
          '.ol-viewport', '#map',
          '#identifyToggleBtn', '.identifyToggle-btn',
          '#sidebar', '.sidebar', '#desktopSidebarToggle', '#sidebarHeaderToggle', '#sidebarToggle', '#mobileMenuBtn',
          '.site-header', '.top-search', '.ui-autocomplete',
          '.ol-popup',
          '.modal', '.modal-content', '.modal-backdrop', // ⚡ Standard modals
          '[class*="modal"]', '[id*="modal"]', // ⚡ Generic modals (Street View, etc)
          '#parcelInfoShowChipBtn', '.parcel-info-chip' // ⚡ Restore interaction chip
        ];

        const isExempt = exemptSelectors.some(sel => {
          try {
            return e.target.closest(sel);
          } catch (err) {
            return false;
          }
        });

        // ⚡ Additional safety: ignore if target is no longer in DOM (temporary export links)
        if (!document.body.contains(e.target)) return;

        if (isExempt) return;

        // Close for ANY other click outside
        this.close();
      }
    });
  }

  /**
   * Drag start handler
   */
  _onDragStart(e) {
    if (window.innerWidth >= 1025) return; // Disable drag on desktop sidebar
    this.isDragging = true;
    this.startY = e.type.includes('mouse') ? e.clientY : e.touches[0].clientY;
    this.lastY = this.startY;
    this.lastTimestamp = performance.now();
    this.velocity = 0;
    this.container.classList.add('dragging');
  }

  /**
   * Drag move handler
   */
  _onDragMove(e) {
    if (!this.isDragging) return;

    const timestamp = performance.now();
    this.currentY = e.type.includes('mouse') ? e.clientY : e.touches[0].clientY;

    // Calculate velocity (px/ms)
    const dt = timestamp - this.lastTimestamp;
    const dy = this.currentY - this.lastY;
    if (dt > 0) this.velocity = dy / dt;

    this.lastY = this.currentY;
    this.lastTimestamp = timestamp;

    const deltaY = this.startY - this.currentY;

    // ⚡ 1:1 Professional Follow Logic
    if (this.isExpanded) {
      // If expanded, dragging down collapses. deltaY will be negative.
      if (deltaY < 10) {
        // Limit upward drag when expanded (Rubber banding)
        const offset = deltaY > 0 ? -Math.pow(deltaY, 0.7) : -deltaY;
        this.container.style.transform = `translateX(-50%) translateY(${offset}px)`;
      }
    } else {
      // If collapsed, dragging up expands (deltaY positive), dragging down closes (deltaY negative)
      const offset = deltaY > 0 ? -deltaY : -deltaY;
      this.container.style.transform = `translateX(-50%) translateY(${offset}px)`;
    }
  }

  /**
   * Drag end handler
   */
  _onDragEnd() {
    if (!this.isDragging) return;

    this.isDragging = false;
    this.container.classList.remove('dragging');
    this.container.style.transform = '';

    const deltaY = this.startY - this.currentY;
    const absVelocity = Math.abs(this.velocity);
    const velocityThreshold = 0.5; // Fast flick
    const distanceThreshold = 80;  // Standard distance

    // ⚡ SMART SNAPPING LOGIC
    if (this.isExpanded) {
      // Currently EXPANDED
      if (deltaY < -distanceThreshold || this.velocity > velocityThreshold) {
        // Swipe Down -> Collapse
        this.collapse();
        if (navigator.vibrate) navigator.vibrate(10);
      }
    } else {
      // Currently COLLAPSED
      if (deltaY > distanceThreshold || this.velocity < -velocityThreshold) {
        // Swipe Up -> Expand
        this.expand();
        if (navigator.vibrate) navigator.vibrate(15);
      } else if (deltaY < -distanceThreshold || this.velocity > velocityThreshold) {
        // Swipe Down while collapsed -> Close completely
        this.close();
        if (navigator.vibrate) navigator.vibrate(20);
      }
    }

    this.startY = 0;
    this.currentY = 0;
    this.velocity = 0;
  }

  /**
   * Show the bottom sheet with data
   * @param {Object} data - Feature data to display
   */
  show(data) {
    this.data = data;

    // ⚡ التجهيز للتحديث بدون رمشة (Suppress Transitions)
    const wasVisible = this.container.classList.contains('visible');
    const wasExpanded = this.container.classList.contains('expanded');

    if (wasVisible) {
      this.container.classList.add('no-transition');
    }

    // ⚡ الحفاظ على حالة التوسعة إذا كانت مفتوحة أصلاً
    // ⚡ Desktop: دائمًا موسّع — التفاصيل معروضة بشكل افتراضي
    if (wasExpanded || window.innerWidth >= 1025) {
      this.isExpanded = true;
      this.container.classList.add('expanded');
    }

    // نعيد البناء فقط إذا كانت النافذة في حالة loading أو فارغة أو غير مبنية
    const needsRebuild = !this.summary || !this.actionsEl ||
      this.container.querySelector('.bottom-sheet-loading') ||
      this.container.querySelector('.bottom-sheet-empty');

    if (needsRebuild) {
      this._rebuildContentStructure();
      this._cacheElements();
    }

    // ⚡ If data contains specific actions, render them now
    if (data.actions) {
      this.renderActions(data.actions);
    } else if (!this.currentActions) {
      // Default fallback
      this.renderActions(this._getDefaultActions());
    }

    this._render();

    // إضافة التنسيقات اللازمة
    if (!wasVisible) {
      this.container.classList.add('visible');
      document.body.classList.add('bottom-sheet-active');
      if (this.isExpanded) document.body.classList.add('bottom-sheet-expanded');
      // Small pulse animation on first show only
      this.container.classList.add('pulse');
      setTimeout(() => this.container.classList.remove('pulse'), 400);
    }

    // ⚡ إعادة تفعيل الـ transitions بعد التحديث مع ضمان استقرار الـ DOM
    if (wasVisible) {
      // ننتظر Frame واحد عشان الـ Render يخلص
      requestAnimationFrame(() => {
        // ننتظر Frame ثاني للتأكد أن المتصفح طبق التغييرات بدون Animation
        requestAnimationFrame(() => {
          this.container.classList.remove('no-transition');
        });
      });
    }
  }

  /**
   * Rebuild the content structure after loading state
   */
  _rebuildContentStructure() {
    const contentEl = this.container.querySelector('.bottom-sheet-content');
    if (!contentEl) return;

    contentEl.innerHTML = `
          <!-- Summary Section (Always Visible) -->
          <div class="bottom-sheet-summary">
            <div class="bottom-sheet-header">
              <h3 class="bottom-sheet-title"></h3>
              <p class="bottom-sheet-subtitle"></p>
            </div>
            <div class="bottom-sheet-metric">
              <div class="bottom-sheet-metric-icon">
                <i class="fas fa-ruler-combined"></i>
              </div>
              <div class="bottom-sheet-metric-content">
                <span class="bottom-sheet-metric-label"></span>
                <span class="bottom-sheet-metric-value"></span>
              </div>
            </div>
          </div>
          
          <!-- Action Buttons -->
          <div class="bottom-sheet-actions"></div>
          
          <!-- View Details Button -->
          <button class="bottom-sheet-expand-btn" data-action="expand">
            <i class="fas ${this.isExpanded ? 'fa-chevron-down' : 'fa-chevron-up'}"></i>
            <span>${this.isExpanded ? 'إخفاء التفاصيل' : 'عرض التفاصيل'}</span>
          </button>
          
          <!-- Details Section (Visible when expanded) -->
          <div class="bottom-sheet-details"></div>
        `;

    // Cache elements FIRST before rendering actions
    this._cacheElements();

    // Restore actions
    this.renderActions(this.currentActions || this._getDefaultActions());
  }

  /**
   * Show loading state
   */
  showLoading() {
    const wasVisible = this.isVisible();
    if (wasVisible) this.container.classList.add('no-transition');

    // Rebuild content container with loading state
    const contentEl = this.container.querySelector('.bottom-sheet-content');
    if (contentEl) {
      contentEl.innerHTML = `
              <div class="bottom-sheet-loading">
                <i class="fas fa-spinner fa-spin"></i>
                <span>جاري البحث عن البيانات...</span>
              </div>
            `;
    }
    this.container.classList.add('visible');
    document.body.classList.add('bottom-sheet-active');

    if (wasVisible) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => this.container.classList.remove('no-transition'));
      });
    }
  }

  /**
   * Show empty/no data state
   * @param {string} message - Message to display
   */
  showEmpty(message = 'لا توجد بيانات') {
    const wasVisible = this.isVisible();
    if (wasVisible) this.container.classList.add('no-transition');

    const contentEl = this.container.querySelector('.bottom-sheet-content');
    if (contentEl) {
      contentEl.innerHTML = `
        <div class="bottom-sheet-empty">
          <i class="fas fa-map-marker-alt"></i>
          <span class="bottom-sheet-empty-title">${message}</span>
          <span class="bottom-sheet-empty-desc">المنطقة التي حددتها لا تحتوي على معلومات.</span>
        </div>
      `;
    }
    this.container.classList.add('visible');
    document.body.classList.add('bottom-sheet-active');

    if (wasVisible) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => this.container.classList.remove('no-transition'));
      });
    }
  }

  /**
   * Hide the bottom sheet (keeps data and highlight)
   */
  hide() {
    this.container.classList.remove('visible', 'expanded');
    document.body.classList.remove('bottom-sheet-active', 'bottom-sheet-expanded');
    this.isExpanded = false;
    // Don't clear data or call close callback - this is temporary hide
  }

  /**
   * Close the bottom sheet completely (clears everything)
   */
  close() {
    this.container.classList.remove('visible', 'expanded');
    document.body.classList.remove('bottom-sheet-active', 'bottom-sheet-expanded');
    this.isExpanded = false;
    this.data = null;

    if (this.callbacks.close) {
      this.callbacks.close();
    }
  }

  /**
   * Expand the bottom sheet to show details
   */
  expand() {
    this.isExpanded = true;
    this.container.classList.add('expanded');
    document.body.classList.add('bottom-sheet-expanded');
    this._updateExpandButtonUI();
  }

  /**
   * Collapse the bottom sheet to summary view
   */
  collapse() {
    this.isExpanded = false;
    this.container.classList.remove('expanded');
    document.body.classList.remove('bottom-sheet-expanded');
    this._updateExpandButtonUI();
  }

  /**
   * Toggle between expanded and collapsed states
   */
  toggle() {
    if (this.isExpanded) {
      this.collapse();
    } else {
      this.expand();
    }
  }

  /**
   * Internal helper to update Expand button text and icon
   */
  _updateExpandButtonUI() {
    const btn = this.container.querySelector('.bottom-sheet-expand-btn');
    if (!btn) return;

    const icon = btn.querySelector('i');
    const span = btn.querySelector('span');

    if (this.isExpanded) {
      if (icon) icon.className = 'fas fa-chevron-down';
      if (span) span.textContent = 'إخفاء التفاصيل';
    } else {
      if (icon) icon.className = 'fas fa-chevron-up';
      if (span) span.textContent = 'عرض التفاصيل';
    }
  }

  /**
   * Update the sheet with new data
   * @param {Object} data - New feature data
   */
  setData(data) {
    this.data = data;
    this._render();
  }

  /**
   * Register action callbacks
   * @param {string} type - Action type: 'report', 'navigate', 'close'
   * @param {Function} callback - Callback function
   */
  onAction(type, callback) {
    this.callbacks[type] = callback;
  }

  /**
   * Clear all registered action callbacks
   */
  clearCallbacks() {
    this.callbacks = {
      streetview: null,
      zoom: null,
      report: null,
      hide: null,
      navigate: null,
      close: null,
      share: null,
      'save-loc': null,
      'delete-pin': null
    };
  }

  /**
   * Render the content based on current data
   */
  _render() {
    if (!this.data) return;

    // Ensure DOM is ready
    if (!this.titleEl) {
      this._createDOM();
      this._cacheElements();
    }

    // Title
    this.titleEl.textContent = this.data.title || 'معلومات';

    // Badge / Status
    const badgeEl = this.container.querySelector('.bottom-sheet-badge');
    if (badgeEl) {
      if (this.data.status) {
        badgeEl.textContent = this.data.status.label;
        badgeEl.className = 'bottom-sheet-badge'; // Reset classes
        if (this.data.status.class) badgeEl.classList.add(this.data.status.class);
        if (this.data.status.type) badgeEl.classList.add(`status-${this.data.status.type}`);
        badgeEl.style.display = 'inline-block';
      } else {
        badgeEl.style.display = 'none';
      }
    }

    // Subtitle
    if (this.subtitleEl) {
      this.subtitleEl.innerHTML = this.data.subtitle || '';
      this.subtitleEl.style.display = this.data.subtitle ? 'block' : 'none';
    }

    // Key metric
    if (this.data.keyMetric && this.metricIcon && this.metricLabel && this.metricValue) {
      this.metricIcon.className = `fas ${this.data.keyMetric.icon || 'fa-ruler-combined'}`;
      this.metricLabel.textContent = this.data.keyMetric.label || '';
      this.metricValue.textContent = this.data.keyMetric.value || '';
    }

    // Detail attributes
    if (this.detailsEl && this.data.attributes && this.data.attributes.length > 0) {
      this.detailsEl.innerHTML = this.data.attributes.map(attr => `
        <div class="bottom-sheet-attr">
          <span class="bottom-sheet-attr-key">${attr.key}</span>
          <span class="bottom-sheet-attr-value">${attr.value || '-'}</span>
        </div>
      `).join('');
    } else if (this.detailsEl) {
      this.detailsEl.innerHTML = '';
    }
  }

  /**
   * Get default actions for Parcel Info
   * الترتيب من اليمين لليسار (RTL): إخفاء، تقرير، تقريب، StreetView
   */
  _getDefaultActions() {
    return [
      { id: 'hide', icon: 'fa-solid fa-eye-slash', class: 'secondary', title: 'إخفاء مؤقت' },
      { id: 'report', icon: 'fa-solid fa-image', label: 'تقرير', class: 'primary', title: 'تصدير صورة JPG' },
      { id: 'zoom', icon: 'fa-solid fa-magnifying-glass-plus', class: 'secondary', title: 'تكبير على القطعة' },
      { id: 'streetview', icon: 'fa-solid fa-street-view', class: 'secondary', title: 'Street View' }
    ];
  }

  /**
   * Render action buttons
   * @param {Array} actions - Array of action objects {id, icon, label, class, title}
   */
  renderActions(actions) {
    if (!this.actionsEl) return;
    this.currentActions = actions; // Store for rebuilds

    this.actionsEl.innerHTML = actions.map(action => `
      <button class="bottom-sheet-btn ${action.class || 'secondary'}" data-action="${action.id}" title="${action.title || ''}">
        <i class="${action.icon}"></i>
        ${action.label ? `<span>${action.label}</span>` : ''}
      </button>
    `).join('');
  }

  /**
   * Set loading state for a specific action button
   * @param {string} actionId - The data-action ID of the button
   * @param {boolean} isLoading - Whether to show loading spinner
   */
  setButtonLoading(actionId, isLoading) {
    if (!this.actionsEl) return;
    const btn = this.actionsEl.querySelector(`[data-action="${actionId}"]`);
    if (!btn) return;

    const action = (this.currentActions || []).find(a => a.id === actionId);
    if (!action) return;

    const iconEl = btn.querySelector('i');
    const spanEl = btn.querySelector('span');

    if (isLoading) {
      btn.disabled = true;
      if (iconEl) {
        // Store original class to restore later
        btn.dataset.originalIcon = action.icon;
        iconEl.className = 'fas fa-spinner fa-spin';
      }
      if (spanEl) {
        btn.dataset.originalText = spanEl.textContent;
        spanEl.textContent = 'جاري...';
      }
    } else {
      btn.disabled = false;
      if (iconEl) {
        iconEl.className = btn.dataset.originalIcon || action.icon;
      }
      if (spanEl) {
        spanEl.textContent = btn.dataset.originalText || action.label || '';
      }
    }
  }

  /**
   * Lock/Unlock the sheet to prevent it from closing
   * @param {boolean} isLocked 
   */
  setLocked(isLocked) {
    this.isLocked = !!isLocked;
    if (this.isLocked) {
      this.container.classList.add('locked');
    } else {
      this.container.classList.remove('locked');
    }
  }

  /**
   * Check if sheet is currently visible
   */
  isVisible() {
    return this.container.classList.contains('visible');
  }

  /**
   * Destroy the bottom sheet instance
   */
  destroy() {
    document.body.classList.remove('bottom-sheet-active', 'bottom-sheet-expanded');
    if (this.container && this.container.parentNode) {
      this.container.parentNode.removeChild(this.container);
    }
  }
}

// Export for ES modules
export { BottomSheet };
export default BottomSheet;
